import React, { useState, useMemo, useRef, useEffect } from "react";
import { Trash2, Users, GripVertical, Eye, EyeOff, Search, Plus } from "lucide-react";

export default function CrewAssignmentsSection({ form, setForm, project, onOpenPicker, Section, crewDb = [], onAddMember }) {
  const updCrew = (i, k, v) => setForm(f => ({ ...f, crew_assignments: f.crew_assignments.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const delCrew = (i) => setForm(f => ({ ...f, crew_assignments: f.crew_assignments.filter((_, idx) => idx !== i) }));
  const moveCrew = (from, to) => setForm(f => {
    if (from === to || from < 0 || to < 0 || from >= f.crew_assignments.length || to >= f.crew_assignments.length) return f;
    const next = [...f.crew_assignments];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    return { ...f, crew_assignments: next.map((c, idx) => ({ ...c, sort_order: idx })) };
  });
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const onDragStart = (i) => (e) => { setDragIdx(i); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (i) => (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setOverIdx(i); };
  const onDrop = (i) => (e) => { e.preventDefault(); if (dragIdx !== null) moveCrew(dragIdx, i); setDragIdx(null); setOverIdx(null); };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  // ── Inline crew autocomplete ──
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const [hover, setHover] = useState(0);
  const wrapRef = useRef();
  const inputRef = useRef();
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setFocused(false); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const addedIds = useMemo(() => new Set((form.crew_assignments || []).map(c => c.crew_id)), [form.crew_assignments]);
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return (crewDb || []).filter(m => {
      if (addedIds.has(m.id)) return false;
      return (m.name || "").toLowerCase().includes(s)
        || (m.role || m.position || "").toLowerCase().includes(s)
        || (m.department || "").toLowerCase().includes(s);
    }).slice(0, 8);
  }, [q, crewDb, addedIds]);
  const pick = (m) => {
    onAddMember && onAddMember(m);
    setQ(""); setHover(0);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHover(h => Math.min(matches.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHover(h => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && matches[hover]) { e.preventDefault(); pick(matches[hover]); }
    else if (e.key === "Escape") { setFocused(false); }
  };

  return (
    <Section title="Crew Assignments" defaultOpen={(form.crew_assignments || []).length > 0}>
      <div className="pt-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={onOpenPicker} data-testid="crew-open-picker-btn"
            className="flex items-center gap-2 border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 px-4 py-2 rounded-lg text-sm transition-all">
            <Users size={15}/> Add from Crew Database
          </button>
          {form.crew_assignments.length > 1 && (
            <p className="text-xs text-gray-600">
              Sorted by <span className="text-amber-400/70">Project's dept order</span>, then your drag order. Drag <GripVertical size={11} className="inline text-amber-400/70"/> to reorder within a department.
            </p>
          )}
        </div>

        {/* Inline autocomplete: type-to-add above the list */}
        {onAddMember && (
          <div ref={wrapRef} className="relative">
            <div className="flex items-center gap-2 bg-[#0D0F12] border border-[#28303F] focus-within:border-amber-500/60 rounded-lg px-3 py-2">
              <Search size={14} className="text-gray-500 shrink-0"/>
              <input
                ref={inputRef}
                data-testid="crew-inline-autocomplete-input"
                type="text"
                value={q}
                onChange={e => { setQ(e.target.value); setHover(0); setFocused(true); }}
                onFocus={() => setFocused(true)}
                onKeyDown={onKey}
                placeholder="Type a name, role, or department to add crew…"
                className="flex-1 bg-transparent text-white text-sm placeholder-gray-600 focus:outline-none"
                autoComplete="off"
              />
              {q && (
                <button type="button" onClick={() => { setQ(""); inputRef.current && inputRef.current.focus(); }}
                  className="text-gray-500 hover:text-white text-xs" title="Clear">✕</button>
              )}
            </div>
            {focused && q.trim() && (
              <div data-testid="crew-inline-autocomplete-suggestions"
                className="absolute z-40 left-0 right-0 mt-1 bg-[#14181F] border border-[#28303F] rounded-lg shadow-2xl max-h-64 overflow-y-auto">
                {matches.length > 0 ? matches.map((m, i) => (
                  <button
                    key={m.id}
                    type="button"
                    data-testid="crew-inline-autocomplete-item"
                    onMouseEnter={() => setHover(i)}
                    onMouseDown={(e) => { e.preventDefault(); pick(m); }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${i === hover ? "bg-[#1A1F29]" : "hover:bg-[#1A1F29]"}`}>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">{m.name}</p>
                      <p className="text-gray-500 text-xs truncate">{m.role || m.position}{m.department ? ` · ${m.department}` : ""}</p>
                    </div>
                    <Plus size={14} className="text-amber-400 shrink-0 ml-2" />
                  </button>
                )) : (
                  <div className="px-3 py-3 text-center text-[11px] text-gray-500 leading-relaxed">
                    No matches in the Crew Database.
                    {(crewDb || []).length === 0 && <> Add crew in the <a href="/crew" className="text-amber-400 hover:underline">Crew Database</a> first.</>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-gray-500 uppercase tracking-widest font-mono mr-1">On call sheet:</span>
          <button type="button" data-testid="crew-show-email-toggle"
            onClick={() => setForm(f => ({ ...f, show_crew_email: !f.show_crew_email }))}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-all ${form.show_crew_email ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-[#0D0F12] text-gray-400 border-[#28303F] hover:border-gray-500"}`}>
            {form.show_crew_email ? <Eye size={11}/> : <EyeOff size={11}/>} Crew emails
          </button>
          <button type="button" data-testid="crew-show-phone-toggle"
            onClick={() => setForm(f => ({ ...f, show_crew_phone: !f.show_crew_phone }))}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-all ${form.show_crew_phone ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-[#0D0F12] text-gray-400 border-[#28303F] hover:border-gray-500"}`}>
            {form.show_crew_phone ? <Eye size={11}/> : <EyeOff size={11}/>} Crew phone numbers
          </button>
        </div>
        {form.crew_assignments.length > 0 && (() => {
          const deptOrder = project?.departments || [];
          const idx = Object.fromEntries(deptOrder.map((d, i) => [d, i]));
          const sorted = [...form.crew_assignments].map((c, oi) => ({ c, oi }))
            .sort((a, b) => {
              const di = (idx[a.c.department] ?? 999) - (idx[b.c.department] ?? 999);
              if (di) return di;
              const so = (a.c.sort_order ?? 0) - (b.c.sort_order ?? 0);
              if (so) return so;
              return (a.c.name || "").localeCompare(b.c.name || "");
            });
          const groups = {};
          sorted.forEach(({ c, oi }) => {
            const d = c.department || "— Unassigned —";
            if (!groups[d]) groups[d] = [];
            groups[d].push({ c, oi });
          });
          const setDeptCall = (dept, time) => setForm(f => ({
            ...f,
            department_call_times: { ...(f.department_call_times || {}), [dept]: time },
            crew_assignments: f.crew_assignments.map(cr =>
              (cr.department || "— Unassigned —") === dept ? { ...cr, call_time: time } : cr
            ),
          }));
          return Object.entries(groups).map(([dept, items]) => (
            <div key={dept} data-testid="crew-dept-group" className="mt-2">
              <div className="flex items-center gap-2 mb-1.5">
                <p className="text-[11px] text-amber-400/80 uppercase tracking-widest font-mono">{dept}</p>
                <span className="text-[10px] text-gray-600">Dept call:</span>
                <input data-testid={`dept-call-time-${dept}`}
                  value={(form.department_call_times || {})[dept] || ""}
                  onChange={e => setDeptCall(dept, e.target.value)}
                  className="w-20 bg-[#14181F] border border-[#28303F] rounded px-2 py-0.5 text-amber-400 text-xs font-mono focus:outline-none focus:border-amber-500"
                  placeholder="07:00"
                  title="Sets call time for every member of this department. Individual rows can still be overwritten." />
              </div>
              <div className="space-y-2">
                {items.map(({ c, oi }) => (
                  <div key={oi} data-testid="crew-assign-row" draggable
                    onDragStart={onDragStart(oi)} onDragOver={onDragOver(oi)}
                    onDrop={onDrop(oi)} onDragEnd={onDragEnd}
                    className={`flex items-center gap-2 bg-[#0D0F12] border border-[#28303F] rounded-lg p-3 ${overIdx === oi && dragIdx !== null && dragIdx !== oi ? "!border-amber-500/60 bg-amber-500/5" : ""} ${dragIdx === oi ? "opacity-50" : ""}`}>
                    <GripVertical size={14} className="text-gray-500 cursor-grab shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{c.role || "—"}</p>
                      <p className="text-gray-500 text-xs truncate">{c.name}</p>
                    </div>
                    <input value={c.call_time} onChange={e => updCrew(oi, "call_time", e.target.value)}
                      data-testid={`crew-row-call-time-${oi}`}
                      className="w-20 bg-[#14181F] border border-[#28303F] rounded px-2 py-1 text-amber-400 text-xs font-mono focus:outline-none focus:border-amber-500" placeholder="07:00" />
                    <button onClick={() => delCrew(oi)} className="text-gray-600 hover:text-red-400"><Trash2 size={13}/></button>
                  </div>
                ))}
              </div>
            </div>
          ));
        })()}
      </div>
    </Section>
  );
}
